import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { AuthenticationService } from '../services/authentication.service';
import { ErrorService } from '../services/error.service';
import { NavigationService } from '../services/navigation.service';
import { SpinnerService } from '../services/spinner.service';
import { UserService } from '../services/user.service';
import { ENROLLMENT_FAILED_REASON } from '../models/mfa';
import { isGuestUser } from '../models/guest-user';

@Injectable()
export class CanActivateAuthGuard implements CanActivate {

	constructor(public _userService: UserService,
				public _router: Router,
				public _authenticationService: AuthenticationService,
				private _spinnerService: SpinnerService,
				private _errorService: ErrorService,
				private _navigationService: NavigationService) {
	}

	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
		return new Promise((resolve) => {
			const user = this._userService.getUser();
			if (user && user.token) {
				// XD-62456: each denial returns. Without the return the guard fell through to renewSession(), so a
				// refused route still POSTed IsAlive and could tear the session down on a non-true answer.
				// A guest skips these three flag denials: PermissionGuard (this guard only ever runs inside it)
				// refuses the same pages for a guest by identity AND explains and redirects. Denying here first made
				// PermissionGuard return at `if (!auth)`, so a correctly configured guest was refused silently.
				const guest = isGuestUser(user);
				if (!guest && state.url.includes('invoices') && (!user.inetUser || !user.inetUser.viewInvoices)) {
					resolve(false);
					return;
				} else if (!guest && state.url.includes('address-book') && (!user.inetUser || !user.inetUser.allowAddressBookAccess)) {
					resolve(false);
					return;
				} else if (!guest && state.url.includes('order-templates') && !user.allowRecentTemplates) {
					resolve(false);
					return;
				}
				if (user.type === 'inet') {
					if (state.url === '/' ||
						state.url.includes('work') ||
						state.url.includes('login') ||
						state.url.includes('register') ||
						state.url.includes('settlements') ||
						state.url.includes('estimated-settlements')) {
						this._router.navigate([this._navigationService.resolveDefaultLanding(user)]);
					}
				} else if (user.type === 'worker') {
					if (state.url === '/' ||
						state.url.includes('address-book') ||
						state.url.includes('login') ||
						state.url.includes('register') ||
						state.url.includes('place-order') ||
						state.url.includes('track-parcel') ||
						state.url.includes('order-confirmation') ||
						state.url.includes('track-order') ||
						state.url.includes('route-tracking') ||
						state.url.includes('reports') ||
						state.url.includes('order-templates') ||
						state.url.includes('invoices')) {
						this._router.navigate(['work']);
					}
				}
				return this.renewSession().then((res) => {
					if (res && user.type === 'inet' && user.mfaEnrollmentPending) {
						this.showEnrollmentWizard().then((outcome) => {
							// Every terminal path must settle canActivate. Sign out ends the session and denies.
							if (outcome === 'signedOut') {
								this._authenticationService.performLogout();
								resolve(false);
								return;
							}
							// 'disabled' already tore the session down inside showEnrollmentWizard
							// (authenticationDelete(message) → auth modal).
							if (outcome === 'disabled') {
								resolve(false);
								return;
							}
							// F-192: 'error' must fail closed WITH teardown, not just deny — denying alone left the
							// session live and mfaEnrollmentPending true, so every later navigation (incl. Sign out →
							// /home, behind this guard) re-opened the failing wizard and stacked dialogs.
							// F-199: only tear down if the session is still ours to end — an external teardown
							// (session timer / 401) may have closed the wizard with `undefined` and already shown its
							// own modal; a second teardown would closeAll() that modal and blank it (mirrors F-148).
							// F-198: pass a reason so the login modal explains the failure (a bare teardown closeAll'd
							// the wizard's own error modal and left a blank login screen).
							if (outcome === 'error') {
								if (this._userService.getUser()?.token) {
									this._authenticationService.authenticationDelete(ENROLLMENT_FAILED_REASON);
								}
								resolve(false);
								return;
							}
							// outcome === 'resolved' (enrolled or skipped) → token is now Active. On a refresh
							// straight into the wizard, the post-login data cascade never ran, so run it now
							// before admitting the route; otherwise OrderEntry (and others) read a null
							// selectedCustomer and crash. The session enrollment flag was already cleared by
							// UserService on a genuine resolution; re-assert it defensively.
							const resolvedUser = this._userService.getUser();
							// F-148: getUser() is null once deleteUser() has run — e.g. the session timer fires while
							// the wizard is open, tearing the session down and closing the dialog with undefined.
							// Dereferencing it here threw and hung the navigation; deny instead.
							if (!resolvedUser?.token) {
								resolve(false);
								return;
							}
							resolvedUser.mfaEnrollmentPending = false;
							this._userService.updateUser(resolvedUser);
							// H3 (amonea): enrolment can resolve with a challenge still owed (Optional policy, a
							// surviving factor). The token is still PreAuth, so running the data cascade would 401
							// and the interceptor short-circuit would hang the guard. Route to the challenge via the
							// auth modal instead — the same way the guard's own challenge/notify branches do.
							if (resolvedUser.mfaChallengePending) {
								this._authenticationService.authenticationDelete();
								this._authenticationService.showAuthModal();
								resolve(false);
								return;
							}
							this._spinnerService.startSpinner();
							this._authenticationService.loadInetUserData(resolvedUser).subscribe({
								next: () => { this._spinnerService.stopSpinner(); resolve(true); },
								error: (error) => {
									this._spinnerService.stopSpinner();
									this._errorService.error(error, 'Post-enrollment data load failed', 'modal');
									this._userService.deleteUser();
									this._authenticationService.showAuthModal();
									resolve(false);
								}
							});
						});
						return;
					}
					if (res && user.type === 'inet' && user.mfaChallengePending) {
						this._authenticationService.authenticationDelete();
						this._authenticationService.showAuthModal();
						resolve(false);
						return;
					}
					if (res && user.type === 'inet' && user.mfaNotifyPending) {
						this._authenticationService.authenticationDelete();
						this._authenticationService.showAuthModal();
						resolve(false);
						return;
					}
					resolve(res);
				});
			} else if ((state.url.includes('reset') && route.params['token'] && (route.params['userId'] || route.params['userid'])) || state.url.includes('track-order') || state.url.includes('register') || state.url.includes('attachments')) {
				resolve(true);
			} else {
				this._authenticationService.showAuthModal();
				resolve(state.url.includes('home'));
			}
		});
	}

	/**
	 * Resolves the wizard's terminal outcome so the caller can settle canActivate correctly for every path.
	 * 'resolved' = enrolled or skipped (admit after the data cascade); 'signedOut' = sign out (deny + logout);
	 * 'disabled' = MFA turned off mid-wizard (session already torn down, just deny); 'error' = Start/Setup
	 * failed or the wizard was force-closed (fail CLOSED — the caller tears the session down and shows the
	 * login modal, so it does NOT loop back into the wizard).
	 */
	private showEnrollmentWizard(): Promise<'resolved' | 'signedOut' | 'disabled' | 'error'> {
		return new Promise((resolve) => {
			this._userService.openMfaEnrollmentWizard().subscribe((result) => {
				if (result?.mfaDisabled) {
					// F-134: this promise MUST settle. The old code returned without resolving, leaving the outer
					// canActivate pending forever (recoverable only because authenticationDelete → showAuthModal
					// happened to supersede the stalled navigation).
					this._authenticationService.authenticationDelete(result.message);
					resolve('disabled');
					return;
				}
				if (result?.signedOut) {
					resolve('signedOut');
					return;
				}
				if (result?.enrolled || result?.skipped) {
					resolve('resolved');
					return;
				}
				// F-139: error / force-close — nothing was enrolled or skipped. Fail CLOSED.
				resolve('error');
			});
		});
	}

	renewSession(): Promise<boolean> {
		return new Promise((resolve) => {
			const user = this._userService.getUser();
			if (user && user.token) {
				this._spinnerService.startSpinner();
				return this._authenticationService.renewSession(user).subscribe({
					next: (response: boolean) => {
						this._spinnerService.stopSpinner();
						if (response === true) {
							resolve(true);
						} else {
							this._userService.deleteUser();
							this._authenticationService.showAuthModal();
							resolve(false);
						}
					}, error: (error) => {
						this._errorService.error(error, 'Renew session failed', 'modal');
						this._spinnerService.stopSpinner();
						resolve(false);
					}
				});
			} else {
				resolve(false);
			}
		});
	}
}
